import { Loader } from '@pwc/pwc-deals-ui';
import PropTypes from 'prop-types';

import { Constants } from '@pwc/pwc-deals-web-common';
import { SyncedInfoProvider } from 'common/components/Autosync/SyncedInfoProvider.tsx';
import LoadingError from 'common/components/LoadingError';
import { ENTITY_TYPES } from 'common/constants/General';
import { getCalcEngineIsProcessing } from 'common/engine/reducers/calcEngine';
import {
  getStructuredAnalysisViewLoadingInProcess,
  selectDraftVersion,
  selectSharedVersion,
} from 'common/engine/reducers/structuredAnalysisViewSlice';
import {
  getAdjustmentOptionsForDdl,
  getAdjustmentOptionsForNwc,
  getAdjustmentOptionsForQofE,
} from 'common/engine/selectors/adjustmentSelectors';
import { getLinkedAdjustmentsByCode } from 'common/engine/selectors/linkedAdjustmentsByDocument';
import { saveLinkedAdjustments } from 'common/engine/services/structuredAnalysisLinkedAdjustmentsService';
import { putStructuredAnalyses } from 'common/engine/services/structuredAnalysisService';
import {
  addAnalysisTypePath,
  analysesListPath,
  reportPreviewPath,
} from 'common/services/urlService';
import ConnectedContentContext from 'features/narratives/Context/ConnectedContentContext';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import {
  saveDocumentDescriptionThunk,
  setDescriptionSavingFulfilled,
} from '../../../common/engine/reducers/structuredDocumentDescriptionSlice';
import AnalysisForm from '../common/AnalysisForm';
import { analysisSchema, emptyAnalysis } from '../common/analysisModel';
import {
  getStructuredAnalysisEdit,
  getStructuredDocuments,
} from '../structuredAnalysisEditSlice';

import { AUTO_SAVE_LOCK_ERROR } from '../../../common/components/GlobalError/constants';
import { clearDataAndtransformAllIngestedStructuredDocumentsThunk } from '../../../common/engine/thunks/clearDataAndtransformAllIngestedStructuredDocumentsThunk';
import { getLinksFromNarratives } from '../../narratives/narrativesUtilities';
import { getAnalysesList, getAnalysesSelector } from '../getAnalysesSlice';
import { getFakeClientUser } from '../../../common/redux/security/loggedInUser/loggedInUserSlice';

const EditStructuredAnalysis = ({
  type,
  getReturnPath,
  showLinkedAdjustments,
  documentCode,
  initialPaneTwoAvailable,
}) => {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const history = useHistory();
  const [analysis, setAnalysis] = useState(emptyAnalysis());
  const [currentTags, setCurrentTags] = useState([]);
  const [tagsUpdated, setTagsUpdated] = useState(false);

  const [connectedContentIsChanged, setConnectedContentIsChanged] =
    useState(false);
  const [
    structuredAnalysisDocumentsWithData,
    setStructuredAnalysisDocumentsWithData,
  ] = useState([]);
  const { lastSelectedKFA } = useContext(ConnectedContentContext);

  const [errorType, setErrorType] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingFulfilled, setIsSavingFulfilled] = useState(false);
  const [isSavingRejected, setIsSavingRejected] = useState(false);
  const [narratives, setNarratives] = useState(null);
  const [docsError, setDocsError] = useState(null);
  const isProcessing = useSelector(getCalcEngineIsProcessing);
  const initializingData = useSelector(
    getStructuredAnalysisViewLoadingInProcess
  );
  const initialLinkedAdjustments = useSelector(
    getLinkedAdjustmentsByCode(documentCode)
  );

  const { loading, error, structuredDocuments } = useSelector(
    getStructuredAnalysisEdit
  );

  const { loading: analysesLoading, error: analysesError } =
    useSelector(getAnalysesSelector);

  const defaultStatusValue = 300;
  const gotoViewChart = useCallback(
    () => history.replace(getReturnPath(projectId)),
    [history, projectId, getReturnPath]
  );
  const formRef = useRef();
  const adjustmentOptions = {
    [Constants.QUALITY_OF_EARNINGS]: useSelector(getAdjustmentOptionsForQofE),
    [Constants.NET_WORKING_CAPITAL]: useSelector(getAdjustmentOptionsForNwc),
    [Constants.DEBT_AND_DEBT_LIKE]: useSelector(getAdjustmentOptionsForDdl),
  };

  useEffect(() => {
    if (error) {
      setDocsError(error);
    } else {
      const targetDocumentOptions = structuredDocuments
        .filter(
          (document) =>
            document.hasDetailView === true && document.code !== documentCode
        )
        .map((structuredAnalysisDocument) => ({
          value: structuredAnalysisDocument.code,
          label: structuredAnalysisDocument.displayName,
        }));

      setStructuredAnalysisDocumentsWithData(() => targetDocumentOptions);
      setDocsError(null);
    }
  }, [loading, structuredDocuments, error, documentCode]);

  const setConnectedContentChanged = () => {
    setConnectedContentIsChanged(!connectedContentIsChanged);
  };

  const draftVersion = useSelector(selectDraftVersion(documentCode));
  const clientVersion = useSelector(selectSharedVersion(documentCode));
  const isOnlySharedAvailable = clientVersion && draftVersion === undefined;

  const isFakeClient = useSelector(getFakeClientUser);

  useEffect(() => {
    if (isFakeClient && clientVersion) {
      gotoViewChart();
    } else if (isFakeClient && !clientVersion) {
      history.push(analysesListPath(projectId));
    }
  }, [isFakeClient, gotoViewChart, clientVersion, projectId, history]);

  const isLoading =
    loading || analysesLoading || isProcessing || initializingData;

  const [isAutoSaveCalled, setAutoSaveCalled] = useState(false);

  useEffect(() => {
    if (
      isOnlySharedAvailable &&
      !isLoading &&
      formRef.current &&
      formRef.current.submitCount < 1
    ) {
      formRef.current.submitForm();
    }
  }, [isOnlySharedAvailable, formRef, isLoading]);

  const setAnalysesData = (analysesData) => {
    if (analysesData && analysesData.length > 0) {
      const returnedAnalysis = { ...analysesData[0] };
      setAnalysis(returnedAnalysis);
    } else {
      history.push('/unauthorized');
    }
  };

  useEffect(() => {
    dispatch(getAnalysesList({ projectId, type })).then((data) =>
      setAnalysesData(data.payload)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, type, history, documentCode, dispatch]);

  useEffect(() => {
    dispatch(getStructuredDocuments(projectId));
    const fetchData = async () => {
      try {
        dispatch(
          clearDataAndtransformAllIngestedStructuredDocumentsThunk(
            projectId,
            documentCode,
            false,
            true
          )
        );
        // eslint-disable-next-line no-empty
      } catch (err) {
        setDocsError(err);
      }
    };

    if (projectId) {
      setDocsError(null);
      fetchData();
    }
  }, [dispatch, projectId, documentCode, isAutoSaveCalled]);

  const handleSave = async (formData) => {
    const { body, tags, linkedAdjustments, source, notes, insight, title } =
      formData;

    dispatch(setDescriptionSavingFulfilled(false));
    setIsSaving(true);
    setIsSavingRejected(false);
    try {
      const links = getLinksFromNarratives(
        narratives,
        analysis.contentId,
        lastSelectedKFA
      );
      setCurrentTags(tags);
      setTagsUpdated(true);
      setIsSavingFulfilled(false);
      dispatch(saveDocumentDescriptionThunk(body, true, projectId));
      await putStructuredAnalyses(projectId, {
        contentThreadId: analysis.contentThreadId,
        contentId: analysis.contentId,
        contentSubType: analysis.contentSubType,
        title,
        insight,
        body,
        source,
        notes,
        tags,
        links,
        basisOfPresentation: analysis.basisOfPresentation,
        password: analysis.password,
      });

      await saveLinkedAdjustments(
        projectId,
        initialLinkedAdjustments,
        linkedAdjustments,
        dispatch
      );
      if (isOnlySharedAvailable) {
        setAutoSaveCalled(true);
      }
      setIsSaving(false);
      setIsSavingFulfilled(true);
      setErrorType();
    } catch (e) {
      setIsSaving(false);
      setIsSavingFulfilled(false);
      setIsSavingRejected(true);
      setErrorType(e);
    }
  };

  const contextStatusInfoItem = useMemo(
    () => ({
      userViewStatus: analysis?.userViewStatus,
      versionStatus: analysis?.versionStatus,
      shareWithAll: analysis?.shareWithAll,
      isEditMode: true,
      isViewMode: false,
    }),
    [analysis]
  );

  const redirectToReportPreview = (version) => {
    history.replace(
      reportPreviewPath(
        projectId,
        analysis.contentThreadId,
        version,
        false,
        ENTITY_TYPES.ANALYSIS
      )
    );
  };

  const previewAction = async (values) => {
    await handleSave(values);
    redirectToReportPreview('latest');
  };
  const handleRedirectToView = (autoSaveError) => {
    if (autoSaveError) {
      const { location } = history;
      const prevPath = (location.state && location.state.prevPath) || null;
      if (
        autoSaveError === AUTO_SAVE_LOCK_ERROR &&
        prevPath === addAnalysisTypePath(projectId)
      ) {
        history.replace(addAnalysisTypePath(projectId), {
          autoSaveError,
        });
      } else {
        history.replace(getReturnPath(projectId), {
          autoSaveError,
        });
      }
    } else {
      history.replace(getReturnPath(projectId));
    }
  };

  const initialFormTags = (() => {
    const tagsFromAnalysis = (analysis.tags || []).map((tag) => tag.text);
    return tagsUpdated ? currentTags : tagsFromAnalysis;
  })();

  const initialFormValues = {
    title: analysis.title,
    body: analysis.body || '',
    insight: analysis.insight || '',
    status: analysis.status || defaultStatusValue,
    source: analysis.source || '',
    notes: analysis.notes || '',
    tags: initialFormTags,
    linkedAdjustments: [...initialLinkedAdjustments],
  };

  if (isLoading || (isSaving && isOnlySharedAvailable)) {
    return <Loader dataTestId="structured-loading-indicator" />;
  }

  if (docsError || analysesError) {
    return <LoadingError />;
  }

  return (
    <>
      <SyncedInfoProvider value={contextStatusInfoItem}>
        <Formik
          initialValues={initialFormValues}
          enableReinitialize
          onSubmit={handleSave}
          validationSchema={analysisSchema}
          validateOnChange
          innerRef={formRef}
        >
          {({
            errors,
            touched,
            dirty,
            isValid,
            values,
            setFieldValue,
            submitForm,
          }) => (
            <AnalysisForm
              documentCode={documentCode}
              onCancel={gotoViewChart}
              onDone={gotoViewChart}
              isSaving={isSaving}
              isSavingFulfilled={isSavingFulfilled}
              isSavingRejected={isSavingRejected}
              errorType={errorType}
              errors={errors}
              touched={touched}
              isValid={isValid}
              values={values}
              setFieldValue={setFieldValue}
              showLibraryItemPicker={false}
              showLinkedAdjustments={showLinkedAdjustments}
              showNotes
              projectId={projectId}
              parentContentId={analysis.contentId || 0}
              narratives={narratives}
              setNarratives={setNarratives}
              adjustmentOptions={adjustmentOptions}
              onSaveText="Updating..."
              standbyText="Update"
              analysis={analysis}
              isEditMode
              initialPaneTwoAvailable={initialPaneTwoAvailable}
              setConnectedContentChanged={setConnectedContentChanged}
              targetDocumentOptions={structuredAnalysisDocumentsWithData}
              submitForm={submitForm}
              previewText="Update and preview"
              onPreview={previewAction}
              dirty={dirty}
              handleRedirectToView={handleRedirectToView}
            />
          )}
        </Formik>
      </SyncedInfoProvider>
    </>
  );
};

EditStructuredAnalysis.propTypes = {
  type: PropTypes.number.isRequired,
  getReturnPath: PropTypes.func.isRequired,
  showLinkedAdjustments: PropTypes.bool,
};

EditStructuredAnalysis.defaultProps = {
  showLinkedAdjustments: false,
};
export default EditStructuredAnalysis;
